import React, { useEffect, useState } from "react";
import MasterLayout from "../Sections/MasterLayout";
import Loader from "./Loader";
import {
  CheckCircle,
  Clock,
  Mail,
  Package,
  User,
  X,
  MapPin,
  Phone,
  ShoppingCart,
} from "lucide-react";
import {
  abandone_admin,
  abandone_recovered,
  server_post_json,
} from "../../ServiceConnection/serviceconnection";
import toast from "react-hot-toast";

// Helper: Time ago
const formatTimeAgo = (date) => {
  if (!date) return "—";
  const now = Date.now();
  const diff = now - new Date(date);
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
};

// Status Badge
const StatusBadge = ({ status }) => {
  const configs = {
    active: {
      color: "bg-yellow-100 text-yellow-800",
      icon: Clock,
      label: "Active",
    },
    emailed: {
      color: "bg-blue-100 text-blue-800",
      icon: Mail,
      label: "Emailed",
    },
    recovered: {
      color: "bg-green-100 text-green-800",
      icon: CheckCircle,
      label: "Recovered",
    },
    removed: { color: "bg-red-100 text-red-800", icon: X, label: "Removed" },
    checkout: {
      color: "bg-purple-100 text-purple-800",
      icon: Package,
      label: "Checked Out",
    },
  };

  const config = configs[status] || {
    color: "bg-gray-100 text-gray-800",
    label: status,
  };
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${config.color}`}
    >
      <Icon className="w-4 h-4" />
      {config.label}
    </span>
  );
};

export default function AbandoneOrders() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState(null); // renamed for clarity

  /* ================= FETCH DATA ================= */
  const fetchAbandoned = async () => {
    try {
      setLoading(true);
      const res = await server_post_json(abandone_admin, {});
      const data = res?.data?.data || [];
      setRecords(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load abandoned carts");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAbandoned();
  }, []);

  /* ================= SEARCH ================= */
  const filtered = records.filter((item) => {
    const searchLower = search.toLowerCase();
    const user = item.user || {};
    const product = item.product || {};

    const userName = (
      user.name ||
      user.fullName ||
      user.username ||
      ""
    ).toLowerCase();
    const userEmail = (user.email || "").toLowerCase();
    const productTitle = (product.title || "").toLowerCase();

    return (
      item.user_id?.toLowerCase().includes(searchLower) ||
      userName.includes(searchLower) ||
      userEmail.includes(searchLower) ||
      item.product_id?.toLowerCase().includes(searchLower) ||
      productTitle.includes(searchLower)
    );
  });

  /* ================= MARK RECOVERED ================= */
  const markRecovered = async (user_id) => {
    if (!window.confirm("Mark this user's abandoned cart as recovered?"))
      return;

    try {
      await server_post_json(abandone_recovered, { user_id });
      toast.success("Cart marked as recovered!");
      fetchAbandoned();
    } catch {
      toast.error("Failed to update status");
    }
  };

  return (
    <MasterLayout>
      {loading && <Loader />}

      <div className="px-4 py-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Abandoned Carts
          </h1>
          <p className="text-gray-600">
            Monitor and recover lost sales effectively
          </p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search by name, email, product..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-md px-5 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black transition"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-5 rounded-xl shadow-sm border">
            <p className="text-sm text-gray-600">Active Carts</p>
            <p className="text-2xl font-bold mt-1">
              {records.filter((r) => r.status === "active").length}
            </p>
          </div>
          <div className="bg-white p-5 rounded-xl shadow-sm border">
            <p className="text-sm text-gray-600">Emailed</p>
            <p className="text-2xl font-bold mt-1">
              {records.filter((r) => r.status === "emailed").length}
            </p>
          </div>
          <div className="bg-white p-5 rounded-xl shadow-sm border">
            <p className="text-sm text-gray-600">Recovered</p>
            <p className="text-2xl font-bold mt-1 text-green-600">
              {records.filter((r) => r.status === "recovered").length}
            </p>
          </div>
          <div className="bg-white p-5 rounded-xl shadow-sm border">
            <p className="text-sm text-gray-600">Recovery Rate</p>
            <p className="text-2xl font-bold mt-1">
              {records.filter((r) => r.status === "emailed").length > 0
                ? Math.round(
                    (records.filter((r) => r.status === "recovered").length /
                      records.filter((r) => r.status === "emailed").length) *
                      100
                  ) + "%"
                : "0%"}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                    Customer
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                    Product
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                    Added
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                    Emailed
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-16 text-gray-500">
                      <div className="flex flex-col items-center">
                        <ShoppingCart className="w-16 h-16 text-gray-300 mb-4" />
                        <p className="text-lg">No abandoned carts found</p>
                        <p className="text-sm mt-1">
                          Great job! All customers are checking out.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((item) => {
                    const user = item.user || {};
                    const product = item.product || {};

                    return (
                      <tr
                        key={item._id}
                        className="hover:bg-gray-50 transition"
                      >
                        <td className="px-6 py-5">
                          <button
                            onClick={() => setSelectedItem(item)}
                            className="flex items-center gap-3 hover:translate-x-1 transition"
                          >
                            <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center font-bold">
                              {(user.name || user.email || "U")
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-gray-900">
                               {user.firstName } {user.lastName }
                              </p>
                              <p className="text-sm text-gray-500">
                                {user.email || item.user_id}
                              </p>
                            </div>
                          </button>
                        </td>

                        <td className="px-6 py-5">
                          <div className="flex items-center gap-4">
                            <img
                              src={
                                product.mainImages?.[0] ||
                                "https://via.placeholder.com/80?text=No+Img"
                              }
                              alt={product.title}
                              className="w-16 h-16 object-cover rounded-lg shadow-sm"
                            />
                            <div>
                              <a
                                href={`/product/${item.product_id
                                }`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-indigo-600 hover:underline"
                              >
                                {product.title || "Unknown Product"}
                              </a>
                              <p className="text-sm text-gray-600 font-semibold">
                                ₹{product.price || "??"}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-5">
                          <StatusBadge status={item.status} />
                        </td>

                        <td className="px-6 py-5 text-sm">
                          <p>{new Date(item.addedAt).toLocaleDateString()}</p>
                          <p className="text-xs text-gray-500">
                            {formatTimeAgo(item.addedAt)}
                          </p>
                        </td>

                        <td className="px-6 py-5 text-sm">
                          {item.emailedAt ? (
                            <>
                              <p>
                                {new Date(item.emailedAt).toLocaleDateString()}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatTimeAgo(item.emailedAt)}
                              </p>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td className="px-6 py-5">
                          {item.status === "emailed" && (
                            <button
                              onClick={() => markRecovered(item.user_id)}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Mark Recovered
                            </button>
                          )}
                          {item.status === "recovered" && (
                            <span className="text-green-600 font-medium">
                              ✓ Recovered
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ================= FULL CUSTOMER DETAILS MODAL ================= */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-start mb-8">
                <h2 className="text-2xl font-bold text-gray-900">
                  Customer Abandoned Cart Details
                </h2>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <X className="w-7 h-7" />
                </button>
              </div>

              {(() => {
                const user = selectedItem.user || {};
                const product = selectedItem.product || {};
                const shipping = user.shippingAddress || {};

                return (
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Left: Customer Info */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center text-2xl font-bold">
                          {(user.name || user.email || "U")
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold">
                          {user.firstName } {user.lastName }
                          </h3>
                          <p className="text-gray-600">
                            {user.email || "No email"}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4 border-t pt-4">
                        <div className="flex items-start gap-3">
                          <Phone className="w-5 h-5 text-gray-500 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Phone</p>
                            <p className="font-medium text-[14px]">
                              {user.phone || user.mobile || "Not provided"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <MapPin className="w-5 h-5 text-gray-500 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">
                              Shipping Address
                            </p>
                            {shipping ? (
                              <p className="font-medium text-[14px] leading-relaxed">
                                {shipping}
                                <br />
                              </p>
                            ) : (
                              <p className="text-gray-500 italic">
                                No shipping address saved
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right: Product & Timeline */}
                    <div className="space-y-6">
                      <div>
                        <p className="text-sm text-gray-600 mb-3">
                          Abandoned Product
                        </p>
                        <div className="bg-gray-50 rounded-xl p-4 flex gap-4">
                          <img
                            src={
                              product.mainImages?.[0] ||
                              "https://via.placeholder.com/120"
                            }
                            alt={product.title}
                            className="w-24 h-24 object-cover rounded-lg"
                          />
                          <div>
                            <h4 className="font-semibold text-lg">
                              {product.title || "Unknown Product"}
                            </h4>
                            <p className="text-2xl font-bold mt-1">
                              ₹{product.price || "??"}
                            </p>
                            <a
                              href={`/product/${
                                product.slug || selectedItem.product_id
                              }`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 text-sm hover:underline"
                            >
                              View Product →
                            </a>
                          </div>
                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <p className="text-sm text-gray-600 mb-3">
                          Activity Timeline
                        </p>
                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between">
                            <span>Added to Cart</span>
                            <span className="font-medium">
                              {new Date(selectedItem.addedAt).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Reminder Email Sent</span>
                            <span className="font-medium">
                              {selectedItem.emailedAt
                                ? new Date(
                                    selectedItem.emailedAt
                                  ).toLocaleString()
                                : "Not sent yet"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Current Status</span>
                            <StatusBadge status={selectedItem.status} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="mt-10 flex justify-end gap-3">
                {selectedItem.status === "emailed" && (
                  <button
                    onClick={() => {
                      markRecovered(selectedItem.user_id);
                      setSelectedItem(null);
                    }}
                    className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition"
                  >
                    Mark as Recovered
                  </button>
                )}
                <button
                  onClick={() => setSelectedItem(null)}
                  className="px-6 py-3 bg-gray-800 text-white rounded-xl font-medium hover:bg-gray-900 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MasterLayout>
  );
}
